import {Component, computed, effect, inject, input, output, signal} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {Dialog} from 'primeng/dialog';
import {SharedModule} from 'primeng/api';
import {
  ProviderScoresControllerService,
  ProviderScoresResponse,
  ProviderTenderScoresControllerService,
  TenderPhaseType,
  TenderStatus,
} from '@frontend/shared-api';
import {GenericQuestionnaireComponent} from '../provider-questionnaire/generic-questionnaire/generic-questionnaire.component';
import {LoadingComponent} from '../loading/loading.component';
import {firstValueFrom, Observable} from 'rxjs';
import {shareReplay} from 'rxjs/operators';
import {ToastService} from '@frontend/toast';

export enum ScoresModalMode {
  CUSTOMER = 'customer',
  PROVIDER = 'provider',
}

@Component({
  selector: 'app-subcriteria-scores-modal',
  imports: [TranslocoPipe, Dialog, SharedModule, GenericQuestionnaireComponent, LoadingComponent],
  templateUrl: './subcriteria-scores-modal.component.html',
})
export class SubcriteriaScoresModalComponent {
  private readonly providerScoresService = inject(ProviderScoresControllerService);
  private readonly providerTenderScoresService = inject(ProviderTenderScoresControllerService);
  private readonly toast = inject(ToastService);

  readonly visible = input.required<boolean>();
  readonly projectId = input.required<number>();
  readonly phaseType = input<TenderPhaseType>(TenderPhaseType.RFI);
  readonly toolId = input<string | undefined>();
  readonly toolName = input<string | null | undefined>();
  readonly tenantMode = input<ScoresModalMode>(ScoresModalMode.CUSTOMER);
  readonly tenderId = input<number | undefined>();
  readonly tenderStatus = input<TenderStatus | undefined>();

  readonly visibleChange = output<boolean>();

  protected readonly loading = signal(false);
  protected readonly providerScores = signal<ProviderScoresResponse | undefined>(undefined);
  private readonly currentToolId = signal<string | undefined>(undefined);
  private readonly currentProjectId = signal<number | undefined>(undefined);
  private cachedProviderScores$?: Observable<ProviderScoresResponse>;

  readonly shouldLoadData = computed(() => {
    const toolId = this.toolId();
    const projectId = this.projectId();
    const toolChanged = toolId !== this.currentToolId();
    const projectIdChanged = projectId !== this.currentProjectId();
    const isVisible = this.visible();
    const hasProjectId = !!projectId;
    const isLoading = this.loading();

    const shouldLoadBase = isVisible && hasProjectId && !isLoading;

    return (
      (shouldLoadBase && toolChanged) ||
      (shouldLoadBase && this.tenantMode() === ScoresModalMode.PROVIDER && projectIdChanged)
    );
  });

  constructor() {
    effect(() => {
      if (this.shouldLoadData()) {
        this.loadProviderScores();
      }
    });
  }

  private getCachedProviderScores(): Observable<ProviderScoresResponse> {
    // providers open the modal for a different project each time
    // and thus need to reload the data instead of using cache
    if (this.tenantMode() === ScoresModalMode.PROVIDER) {
      const tenderId = this.tenderId();
      if (!tenderId) {
        this.toast.showError('Error', 'Tender ID is required for provider mode');
        return new Observable((subscriber) => {
          subscriber.error(new Error('tenderId is required when mode is provider'));
        });
      }
      this.cachedProviderScores$ = this.providerTenderScoresService
        .getProviderTenderScoresByPhase({
          tenderId,
          phase: this.phaseType(),
        })
        .pipe(shareReplay(1));
    } else if (!this.cachedProviderScores$) {
      this.cachedProviderScores$ = this.providerScoresService
        .getProviderScoresByPhase({
          projectId: this.projectId(),
          phase: this.phaseType(),
        })
        .pipe(shareReplay(1));
    }
    return this.cachedProviderScores$;
  }

  async loadProviderScores(): Promise<void> {
    if (!this.shouldLoadData()) {
      return;
    }

    this.loading.set(true);
    try {
      const response = await firstValueFrom(this.getCachedProviderScores());
      const directusToolId = this.toolId();
      if (directusToolId) {
        const filteredResponse = this.filterResponseByProvider(response, directusToolId);
        this.providerScores.set(filteredResponse);
        this.currentToolId.set(directusToolId);
      } else {
        this.providerScores.set(response);
        this.currentToolId.set(undefined);
      }

      this.currentProjectId.set(this.projectId());
    } finally {
      this.loading.set(false);
    }
  }

  private filterResponseByProvider(
    response: ProviderScoresResponse,
    directusToolId: string,
  ): ProviderScoresResponse {
    return {
      ...response,
      evaluationCriteria: response.evaluationCriteria.map((criteria) => ({
        ...criteria,
        subCriteria: criteria.subCriteria.map((subCriteria) => ({
          ...subCriteria,
          attributes: subCriteria.attributes.map((attribute) => ({
            ...attribute,
            scores: attribute.scores.filter((score) => score.hrToolDirectusId === directusToolId),
          })),
        })),
      })),
    };
  }
}
